/**
 * The idempotent receiving requests that were prepared or sent and may not have been answered (batch 8 T12c2, #3033): a receipt,
 * a hold decision and a close-short.
 *
 * Each is a keyed command: the server binds the `Idempotency-Key` to the WHOLE request, answers a repeat from its record and refuses
 * the key with another payload. That only helps if the repeat is the same key with the same payload to the same target, so the exact
 * request is written down BEFORE it is sent and kept until the server has decided:
 *
 *  - accepted (2xx, also a replay): removed;
 *  - definitely refused (a 4xx the server read, nothing written): removed; the user corrects it and a new request gets a new key;
 *  - ambiguous (lost connection, timeout, 5xx, a concurrency rollback, a lock or session guard): KEPT. The screen offers to send the
 *    identical request again or to abandon it knowingly. It never sends the same intent under a fresh key, because if the first
 *    attempt committed that would receive the goods twice.
 *
 * Durability is a precondition of sending, not a courtesy. `putEntry` returns `stored` only when the document was written AND read
 * back identical; any other answer blocks the dispatch. There is no memory fallback: a browser that refuses storage blocks receiving,
 * and says so. A document that cannot be parsed is `corrupt`: nothing in it is used and nothing new starts until the user knowingly
 * discards the damaged record, because an unknown earlier request cannot be ruled out. Nothing is ever dropped silently.
 *
 * Ownership. An entry belongs to the session that wrote it (tenant, user, session family; never a token or the in-memory epoch) and
 * lives in a document named after that owner. Another tenant, user, a new login or a signed-out page cannot find, overwrite or delete
 * it. A reload of the same session finds it again with the exact key, body and target. The journal holds no credential: only ids,
 * quantities, reasons, notes, the key and times. Removal is compare-and-delete, so an old answer never removes a newer intent.
 *
 * Storage is `sessionStorage` (per tab) and the only thing a reload can find: every read parses the stored document (it is bounded in
 * entries and size). The page additionally remembers, per owner, the unresolved requests it has written or read (the "tombstones").
 * That memory NEVER allows a send: it only keeps an erased, truncated or replaced document from turning into silence. When the
 * stored document no longer holds a remembered request, the request stays visible as `lost` (or `replaced`) and
 *
 *  - a new request for the same slot is refused (`pending`), so an unresolved intent never gets a fresh key;
 *  - an explicit retry first writes the exact remembered key and body back, reads it back, and only then sends it (`restoreEntry`);
 *  - a replaced slot (storage holds another request than the page wrote) fails closed until the user abandons the remembered one.
 *
 * Tombstones are forgotten only by `removeEntry` (the server decided, or the user knowingly abandoned) and by the end of the page.
 * They are not forgotten by an unmount, a lock or a transient auth change, and never cross an owner.
 *
 * Hard limit: if the stored document is erased AND the page is reloaded (or the tab is closed), nothing remembers the request. The
 * portal cannot know it existed and cannot recover its key. The server still holds its idempotent record; the only way to find out
 * whether it committed is the receipt history, which is why the copy tells the user to check it.
 *
 * Nothing here ever sends a request.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { ownerEquals, ownerId, type ReceivingOwner } from './receivingSession';
import {
  CLOSE_SHORT_REASON_MAX,
  MAX_QUANTITY,
  MAX_RECEIPT_LINES,
  NOTE_MAX,
  REFERENCE_MAX,
  REJECT_REASONS,
  type OperationKind,
} from './receivingTypes';

export type JournalStatus = 'ok' | 'unavailable' | 'corrupt';
export type PutStatus = 'stored' | 'conflict' | 'full' | 'corrupt' | 'storage_failed';
/**
 * Whether storage still holds the request this page knows:
 *  - `kept`: stored, exactly the remembered request;
 *  - `lost`: the page remembers it, the stored document does not hold it (erased, truncated, unreadable);
 *  - `replaced`: the stored slot holds a different request than the one the page wrote.
 * Only `kept` may be sent.
 */
export type EntryDurability = 'kept' | 'lost' | 'replaced';
export type RemoveStatus = 'removed' | 'absent' | 'failed';

export interface JournalRequest {
  /** The `Idempotency-Key`: fixed for the life of the operation. */
  readonly key: string;
  /** The exact payload. Never rebuilt, never edited. */
  readonly body: unknown;
}

export interface JournalEntry {
  readonly kind: OperationKind;
  /** The address within the owner's journal: `kind:target`. */
  readonly slot: string;
  /** The purchase order the operation belongs to. */
  readonly purchaseOrderId: string;
  /** The purchase order (receive, close short) or the receipt line (hold decision) the request is addressed to. */
  readonly target: string;
  readonly owner: ReceivingOwner;
  readonly request: JournalRequest;
  readonly startedAt: string;
  /** How many times this very request was sent. */
  readonly attempts: number;
  readonly durability: EntryDurability;
}

export interface JournalView {
  readonly status: JournalStatus;
  readonly entries: readonly JournalEntry[];
  /** Slots with a request on its way in this page (possibly started by a screen that has since unmounted). */
  readonly inFlight: ReadonlySet<string>;
}

export const MAX_JOURNAL_ENTRIES = 20;
const MAX_DOCUMENT_CHARS = 256 * 1024;
const PREFIX = 'os-buddy:purchase-receipt-journal:v1:';

export const slotOf = (kind: OperationKind, target: string): string => `${kind}:${target}`;

export const EMPTY_VIEW: JournalView = Object.freeze({ status: 'ok', entries: [], inFlight: new Set<string>() });

// ---------------------------------------------------------------------------
// Validation of what is read back (the strict schemas of the server, so a stored request is one the server would accept)
// ---------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const ID = /^[A-Za-z0-9_-]{1,100}$/;
export const isId = (value: unknown): value is string => typeof value === 'string' && ID.test(value);
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;
const isKey = (value: unknown): value is string => typeof value === 'string' && value.length > 0 && value.length <= 200 && !CONTROL.test(value);
const onlyKeys = (value: Record<string, unknown>, allowed: readonly string[]): boolean => Object.keys(value).every((key) => allowed.includes(key));
const quantity = (value: unknown, min: 0 | 1): boolean => typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= MAX_QUANTITY;
const text = (value: unknown, max: number): boolean => typeof value === 'string' && value.length > 0 && value.length <= max;
const optionalText = (value: unknown, max: number): boolean => value === undefined || text(value, max);
const reasonOk = (value: unknown): boolean => typeof value === 'string' && (REJECT_REASONS as readonly string[]).includes(value);

function validReceiveLine(line: unknown): boolean {
  if (!isRecord(line)) return false;
  if (!onlyKeys(line, ['lineId', 'quantityArrived', 'quantityAccepted', 'quantityRejected', 'quantityHeld', 'rejectReason', 'qualityObserved', 'note'])) return false;
  return (
    isId(line['lineId']) &&
    quantity(line['quantityArrived'], 1) &&
    quantity(line['quantityAccepted'], 0) &&
    quantity(line['quantityRejected'], 0) &&
    quantity(line['quantityHeld'], 0) &&
    (line['rejectReason'] === undefined || reasonOk(line['rejectReason'])) &&
    typeof line['qualityObserved'] === 'boolean' &&
    optionalText(line['note'], NOTE_MAX)
  );
}

const validators: Record<OperationKind, (body: unknown) => boolean> = {
  receive: (body) =>
    isRecord(body) &&
    onlyKeys(body, ['lines', 'deliveryNoteReference', 'note']) &&
    Array.isArray(body['lines']) &&
    body['lines'].length >= 1 &&
    body['lines'].length <= MAX_RECEIPT_LINES &&
    (body['lines'] as unknown[]).every(validReceiveLine) &&
    new Set((body['lines'] as { lineId: string }[]).map((line) => line.lineId)).size === body['lines'].length &&
    optionalText(body['deliveryNoteReference'], REFERENCE_MAX) &&
    optionalText(body['note'], NOTE_MAX),
  resolve_hold: (body) =>
    isRecord(body) &&
    onlyKeys(body, ['decision', 'quantity', 'rejectReason', 'note']) &&
    (body['decision'] === 'accept' || body['decision'] === 'reject') &&
    quantity(body['quantity'], 1) &&
    (body['decision'] === 'reject' ? reasonOk(body['rejectReason']) : body['rejectReason'] === undefined) &&
    optionalText(body['note'], NOTE_MAX),
  close_short: (body) =>
    isRecord(body) &&
    onlyKeys(body, ['reason', 'lineIds']) &&
    text(body['reason'], CLOSE_SHORT_REASON_MAX) &&
    (body['lineIds'] === undefined ||
      (Array.isArray(body['lineIds']) && body['lineIds'].length >= 1 && body['lineIds'].length <= MAX_RECEIPT_LINES && (body['lineIds'] as unknown[]).every(isId))),
};

/** An entry as persisted. The owner is the document's, never the entry's own claim. */
interface StoredEntry {
  kind: OperationKind;
  purchaseOrderId: string;
  target: string;
  key: string;
  body: unknown;
  startedAt: string;
  attempts: number;
}

function parseDocument(raw: string, owner: ReceivingOwner): StoredEntry[] | null {
  if (raw.length > MAX_DOCUMENT_CHARS) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed['v'] !== 1 || !isRecord(parsed['owner'])) return null;
  const claimed = parsed['owner'];
  if (!ownerEquals(owner, { tenantId: String(claimed['tenantId']), userId: String(claimed['userId']), sessionFamilyId: String(claimed['sessionFamilyId']) })) {
    return null;
  }
  const entries = parsed['entries'];
  if (!Array.isArray(entries) || entries.length > MAX_JOURNAL_ENTRIES) return null;
  const slots = new Set<string>();
  const result: StoredEntry[] = [];
  for (const entry of entries as unknown[]) {
    if (!isRecord(entry)) return null;
    const { kind, purchaseOrderId, target, key, body, startedAt, attempts } = entry;
    if (kind !== 'receive' && kind !== 'resolve_hold' && kind !== 'close_short') return null;
    if (!isId(purchaseOrderId) || !isId(target) || !isKey(key)) return null;
    if (kind !== 'resolve_hold' && target !== purchaseOrderId) return null;
    if (typeof startedAt !== 'string' || Number.isNaN(Date.parse(startedAt))) return null;
    if (typeof attempts !== 'number' || !Number.isSafeInteger(attempts) || attempts < 0 || attempts > 1000) return null;
    if (!validators[kind](body)) return null;
    const slot = slotOf(kind, target);
    if (slots.has(slot)) return null;
    slots.add(slot);
    result.push({ kind, purchaseOrderId, target, key, body, startedAt, attempts });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Storage access and the view
// ---------------------------------------------------------------------------

const listeners = new Set<() => void>();
/** Requests on their way in this page, per owner. */
const inFlightByOwner = new Map<string, Map<string, string>>();
/** Owners whose last write or removal the browser refused. Cleared by the next write that is kept. */
const writeFailed = new Set<string>();
/** Unresolved requests this page wrote or read, per owner and slot. Never a licence to send: see the header. */
const unresolvedByOwner = new Map<string, Map<string, StoredEntry>>();
const memoryVersion = new Map<string, number>();
const viewCache = new Map<string, { raw: string | null | 'unreadable'; flights: string; failed: boolean; memory: number; view: JournalView }>();
const PROBE_ADDRESS = 'os-buddy:purchase-receipt-journal-probe';

const addressOf = (owner: ReceivingOwner): string => `${PREFIX}${[owner.tenantId, owner.userId, owner.sessionFamilyId].map(encodeURIComponent).join(':')}`;

function notify(): void {
  for (const listener of [...listeners]) listener();
}

function flightsOf(owner: ReceivingOwner): Map<string, string> {
  const id = ownerId(owner);
  let flights = inFlightByOwner.get(id);
  if (!flights) {
    flights = new Map();
    inFlightByOwner.set(id, flights);
  }
  return flights;
}

function unresolvedOf(owner: ReceivingOwner): Map<string, StoredEntry> {
  const id = ownerId(owner);
  let held = unresolvedByOwner.get(id);
  if (!held) {
    held = new Map();
    unresolvedByOwner.set(id, held);
  }
  return held;
}

const bumpMemory = (owner: ReceivingOwner): void => {
  const id = ownerId(owner);
  memoryVersion.set(id, (memoryVersion.get(id) ?? 0) + 1);
};

const sameRequest = (a: StoredEntry, b: StoredEntry): boolean =>
  a.key === b.key && a.kind === b.kind && a.target === b.target && a.purchaseOrderId === b.purchaseOrderId && JSON.stringify(a.body) === JSON.stringify(b.body);

type Read = { ok: true; raw: string | null } | { ok: false };

function readRaw(owner: ReceivingOwner): Read {
  try {
    return { ok: true, raw: window.sessionStorage.getItem(addressOf(owner)) };
  } catch {
    return { ok: false };
  }
}

function toEntries(stored: readonly StoredEntry[], owner: ReceivingOwner, durability: EntryDurability): JournalEntry[] {
  return stored.map((entry) => ({
    durability,
    kind: entry.kind,
    slot: slotOf(entry.kind, entry.target),
    purchaseOrderId: entry.purchaseOrderId,
    target: entry.target,
    owner,
    request: { key: entry.key, body: entry.body },
    startedAt: entry.startedAt,
    attempts: entry.attempts,
  }));
}

/** The journal of `owner` as storage holds it right now. The same object is returned until storage or a flight changes. */
export function getJournalView(owner: ReceivingOwner | null): JournalView {
  if (owner === null) return EMPTY_VIEW;
  const id = ownerId(owner);
  const read = readRaw(owner);
  const raw = read.ok ? read.raw : 'unreadable';
  const flights = [...flightsOf(owner).entries()].map(([slot, key]) => `${slot}=${key}`).sort().join('|');
  const failed = writeFailed.has(id);
  const cached = viewCache.get(id);
  if (cached && cached.raw === raw && cached.flights === flights && cached.failed === failed && cached.memory === (memoryVersion.get(id) ?? 0)) return cached.view;

  const inFlight = new Set(flightsOf(owner).keys());
  const remembered = unresolvedOf(owner);
  let view: JournalView;
  if (raw === 'unreadable') {
    view = { status: 'unavailable', entries: toEntries([...remembered.values()], owner, 'lost'), inFlight };
  } else if (raw === null) {
    view = { status: failed ? 'unavailable' : 'ok', entries: toEntries([...remembered.values()], owner, 'lost'), inFlight };
  } else {
    const stored = parseDocument(raw, owner);
    if (stored === null) {
      view = { status: 'corrupt', entries: [], inFlight };
    } else {
      const entries: JournalEntry[] = [];
      const seen = new Set<string>();
      for (const entry of stored) {
        const slot = slotOf(entry.kind, entry.target);
        seen.add(slot);
        const known = remembered.get(slot);
        if (!known) {
          // First sight (a reload, or another page's write): remember it, so that its later disappearance is noticed.
          remembered.set(slot, entry);
          bumpMemory(owner);
          entries.push(...toEntries([entry], owner, 'kept'));
        } else if (sameRequest(known, entry)) {
          if (known.attempts !== entry.attempts) {
            remembered.set(slot, entry);
            bumpMemory(owner);
          }
          entries.push(...toEntries([entry], owner, 'kept'));
        } else {
          entries.push(...toEntries([known], owner, 'replaced'));
        }
      }
      entries.push(...toEntries([...remembered.entries()].filter(([slot]) => !seen.has(slot)).map(([, entry]) => entry), owner, 'lost'));
      view = { status: failed ? 'unavailable' : 'ok', entries, inFlight };
    }
  }
  viewCache.set(id, { raw, flights, failed, memory: memoryVersion.get(id) ?? 0, view });
  return view;
}

export function subscribeJournal(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The journal of `owner`, live: re-renders when an entry, a flight or the storage state changes. */
export function useJournalView(owner: ReceivingOwner | null): JournalView {
  const snapshot = useCallback(() => getJournalView(owner), [owner]);
  return useSyncExternalStore(subscribeJournal, snapshot, snapshot);
}

const serialize = (owner: ReceivingOwner, entries: readonly StoredEntry[]): string =>
  JSON.stringify({
    v: 1,
    owner: { tenantId: owner.tenantId, userId: owner.userId, sessionFamilyId: owner.sessionFamilyId },
    entries,
  });

/**
 * Writes `entries` (or removes the document when there are none) and reads the result back. True only when storage now holds
 * exactly what was written. On a failed write the previous document is restored as far as the browser allows.
 */
function commit(owner: ReceivingOwner, entries: readonly StoredEntry[], previousRaw: string | null): boolean {
  const address = addressOf(owner);
  const id = ownerId(owner);
  try {
    if (entries.length === 0) {
      window.sessionStorage.removeItem(address);
      if (window.sessionStorage.getItem(address) !== null) throw new Error('journal document not removed');
    } else {
      const textValue = serialize(owner, entries);
      if (textValue.length > MAX_DOCUMENT_CHARS) throw new Error('journal document too large');
      window.sessionStorage.setItem(address, textValue);
      if (window.sessionStorage.getItem(address) !== textValue) throw new Error('journal document not kept');
    }
    writeFailed.delete(id);
    return true;
  } catch {
    writeFailed.add(id);
    try {
      if (previousRaw === null) window.sessionStorage.removeItem(address);
      else window.sessionStorage.setItem(address, previousRaw);
    } catch {
      // The browser keeps refusing; the document stays as it is and the view says so.
    }
    return false;
  }
}

export interface NewEntry {
  kind: OperationKind;
  purchaseOrderId: string;
  target: string;
  request: JournalRequest;
}

/**
 * Appends `next` to `current`, reads the document back through the parser and checks that a reload would find exactly this request.
 * `stored` only then; on any failure the previous document is restored and the owner is marked as having a storage problem.
 */
function writeVerified(owner: ReceivingOwner, current: readonly StoredEntry[], next: StoredEntry, previousRaw: string | null): PutStatus {
  const slot = slotOf(next.kind, next.target);
  const kept = commit(owner, [...current, next], previousRaw);
  const back = kept ? readRaw(owner) : null;
  const parsedBack = back && back.ok && back.raw !== null ? parseDocument(back.raw, owner) : null;
  const found = parsedBack?.find((entry) => slotOf(entry.kind, entry.target) === slot);
  const identical = found !== undefined && sameRequest(found, next);
  if (!kept || !identical) {
    if (kept) commit(owner, [...current], previousRaw);
    writeFailed.add(ownerId(owner));
    notify();
    return 'storage_failed';
  }
  unresolvedOf(owner).set(slot, next);
  bumpMemory(owner);
  notify();
  return 'stored';
}

/** What storage holds now, or the reason it cannot be used. */
function readCurrent(owner: ReceivingOwner): { ok: true; current: StoredEntry[]; raw: string | null } | { ok: false; status: PutStatus } {
  const read = readRaw(owner);
  if (!read.ok) {
    writeFailed.add(ownerId(owner));
    notify();
    return { ok: false, status: 'storage_failed' };
  }
  if (read.raw === null) return { ok: true, current: [], raw: null };
  const parsed = parseDocument(read.raw, owner);
  return parsed === null ? { ok: false, status: 'corrupt' } : { ok: true, current: parsed, raw: read.raw };
}

/**
 * Writes the request down BEFORE it is sent. `stored` means it was written and read back identical; every other answer means
 * nothing may be sent. Never overwrites an unresolved request of the same slot, stored or remembered by this page.
 */
export function putEntry(owner: ReceivingOwner, input: NewEntry): PutStatus {
  const read = readCurrent(owner);
  if (!read.ok) return read.status;
  const slot = slotOf(input.kind, input.target);
  const remembered = unresolvedOf(owner);
  if (read.current.some((entry) => slotOf(entry.kind, entry.target) === slot) || remembered.has(slot)) return 'conflict';
  const stored = new Set(read.current.map((entry) => slotOf(entry.kind, entry.target)));
  if (read.current.length + [...remembered.keys()].filter((known) => !stored.has(known)).length >= MAX_JOURNAL_ENTRIES) return 'full';
  const next: StoredEntry = {
    kind: input.kind,
    purchaseOrderId: input.purchaseOrderId,
    target: input.target,
    key: input.request.key,
    body: input.request.body,
    startedAt: new Date().toISOString(),
    attempts: 0,
  };
  if (!validators[input.kind](next.body) || !isId(next.purchaseOrderId) || !isId(next.target) || !isKey(next.key)) return 'storage_failed';
  return writeVerified(owner, read.current, next, read.raw);
}

/**
 * Writes a request this page remembers but storage no longer holds back, with its exact key and body, and reads it back. `stored`
 * is the precondition of sending it again. A slot that storage holds with another request is `conflict` (nothing is overwritten).
 */
export function restoreEntry(owner: ReceivingOwner, slot: string, key: string): PutStatus {
  const known = unresolvedOf(owner).get(slot);
  if (!known || known.key !== key) return 'conflict';
  const read = readCurrent(owner);
  if (!read.ok) return read.status;
  const present = read.current.find((entry) => slotOf(entry.kind, entry.target) === slot);
  if (present) return sameRequest(present, known) ? 'stored' : 'conflict';
  if (read.current.length >= MAX_JOURNAL_ENTRIES) return 'full';
  return writeVerified(owner, read.current, known, read.raw);
}

/**
 * Explicit recovery of the storage state without a reload: checks that the browser reads and keeps a record again (writes a probe,
 * reads it back, removes it). It never writes a request and never sends one; the next explicit action still has to persist and read
 * back its own entry. True when the "storage failed" mark was cleared.
 */
export function probeJournalStorage(owner: ReceivingOwner): boolean {
  const id = ownerId(owner);
  const token = `probe:${Date.now()}`;
  try {
    window.sessionStorage.getItem(addressOf(owner));
    window.sessionStorage.setItem(PROBE_ADDRESS, token);
    const kept = window.sessionStorage.getItem(PROBE_ADDRESS) === token;
    window.sessionStorage.removeItem(PROBE_ADDRESS);
    if (!kept || window.sessionStorage.getItem(PROBE_ADDRESS) !== null) throw new Error('probe not kept');
  } catch {
    try {
      window.sessionStorage.removeItem(PROBE_ADDRESS);
    } catch {
      // Still refusing.
    }
    writeFailed.add(id);
    notify();
    return false;
  }
  writeFailed.delete(id);
  notify();
  return true;
}

/** Counts one more send of exactly this request. Best effort: the attempt count is information, not a safety property. */
export function recordAttempt(owner: ReceivingOwner, slot: string, key: string): void {
  const known = unresolvedOf(owner).get(slot);
  if (known && known.key === key) {
    unresolvedOf(owner).set(slot, { ...known, attempts: known.attempts + 1 });
    bumpMemory(owner);
  }
  const read = readRaw(owner);
  if (!read.ok || read.raw === null) return;
  const parsed = parseDocument(read.raw, owner);
  if (parsed === null) return;
  const next = parsed.map((entry) => (slotOf(entry.kind, entry.target) === slot && entry.key === key ? { ...entry, attempts: entry.attempts + 1 } : entry));
  commit(owner, next, read.raw);
  notify();
}

/**
 * Compare-and-delete: only the entry that still holds `key` is removed, from storage and from the page's memory. Called only when
 * the server decided or the user knowingly abandoned the request.
 */
export function removeEntry(owner: ReceivingOwner, slot: string, key: string): RemoveStatus {
  const status = removeFromStorage(owner, slot, key);
  // Forgotten only after storage is done with it: a view read in between would otherwise adopt the stored copy again.
  const held = unresolvedOf(owner);
  if (held.get(slot)?.key === key) {
    held.delete(slot);
    bumpMemory(owner);
  }
  notify();
  return status;
}

function removeFromStorage(owner: ReceivingOwner, slot: string, key: string): RemoveStatus {
  const read = readRaw(owner);
  if (!read.ok) {
    writeFailed.add(ownerId(owner));
    return 'failed';
  }
  if (read.raw === null) return 'absent';
  const parsed = parseDocument(read.raw, owner);
  if (parsed === null) return 'failed';
  const next = parsed.filter((entry) => !(slotOf(entry.kind, entry.target) === slot && entry.key === key));
  if (next.length === parsed.length) return 'absent';
  return commit(owner, next, read.raw) ? 'removed' : 'failed';
}

/** The user knowingly discarded a damaged record: its content was never used. */
export function dropCorruptJournal(owner: ReceivingOwner): void {
  const read = readRaw(owner);
  if (!read.ok || read.raw === null) return;
  if (parseDocument(read.raw, owner) !== null) return;
  try {
    window.sessionStorage.removeItem(addressOf(owner));
  } catch {
    // Still unreadable: the notice stays.
    return;
  }
  notify();
}

/** Marks a request as on its way. False when another request of the slot already is. */
export function beginFlight(owner: ReceivingOwner, slot: string, key: string): boolean {
  const flights = flightsOf(owner);
  if (flights.has(slot)) return false;
  flights.set(slot, key);
  notify();
  return true;
}

export function endFlight(owner: ReceivingOwner, slot: string, key: string): void {
  const flights = flightsOf(owner);
  if (flights.get(slot) !== key) return;
  flights.delete(slot);
  notify();
}

/** Test support: forgets every flight and every stored document. */
export function resetReceivingJournalForTests(): void {
  inFlightByOwner.clear();
  unresolvedByOwner.clear();
  memoryVersion.clear();
  writeFailed.clear();
  viewCache.clear();
  try {
    const stale: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(PREFIX)) stale.push(key);
    }
    for (const key of stale) window.sessionStorage.removeItem(key);
  } catch {
    // Nothing stored, nothing to remove.
  }
  notify();
}

/** Test support: re-renders every subscriber (storage edited behind the page's back raises no event of its own). */
export function refreshReceivingJournalForTests(): void {
  notify();
}

/** Test support: a page reload. The memory of the page is gone, the documents in `sessionStorage` stay. */
export function resetReceivingJournalMemoryForReload(): void {
  inFlightByOwner.clear();
  unresolvedByOwner.clear();
  memoryVersion.clear();
  writeFailed.clear();
  viewCache.clear();
  notify();
}
